import {
	AppWindow,
	Building2,
	CircleDashed,
	DollarSign,
	Folder,
	type LucideIcon,
	Tags,
	UserRound,
} from "lucide-react";
import type { EnumFilterField } from "./types";

export type ValueSource = "static" | "derived";

export type FieldKey = EnumFilterField;

export type EnumFieldDef = {
	kind: "enum";
	key: EnumFilterField;
	label: string;
	/** Lowercase plural noun used by multi-value labels ("Multiple apps"). */
	pluralLabel: string;
	icon: LucideIcon;
	source: ValueSource;
	staticValues?: ReadonlyArray<string>;
	/**
	 * Tailwind width class for the value-picker pane. Defaults to `w-56` when
	 * omitted — matter/client need extra width to show the matter description /
	 * full client name without truncating.
	 */
	valueWidthClass?: string;
};

/**
 * Rich option row: a checklist value that ships a separate display label and
 * (optionally) a smaller sublabel rendered below it. The filter value
 * is `value`; rendering and substring search use `label + sublabel`. Use
 * `normalizeOption` to coerce plain strings into this shape.
 */
export type OptionItem = {
	value: string;
	label: string;
	sublabel?: string;
};

export type FieldOptionList = ReadonlyArray<string> | ReadonlyArray<OptionItem>;

export function normalizeOption(o: string | OptionItem): OptionItem {
	return typeof o === "string" ? { value: o, label: o } : o;
}

export function normalizeOptions(
	list: FieldOptionList,
): ReadonlyArray<OptionItem> {
	return (list as ReadonlyArray<string | OptionItem>).map(normalizeOption);
}

export type FieldDef = EnumFieldDef;

export const FIELD_DEFS: ReadonlyArray<FieldDef> = [
	{
		kind: "enum",
		key: "status",
		label: "Status",
		pluralLabel: "statuses",
		icon: CircleDashed,
		source: "static",
		staticValues: ["tracked", "reviewed", "exported", "deleted"],
	},
	{
		kind: "enum",
		key: "timekeeper",
		label: "Timekeeper",
		pluralLabel: "timekeepers",
		icon: UserRound,
		source: "derived",
	},
	{
		kind: "enum",
		key: "application",
		label: "App",
		pluralLabel: "apps",
		icon: AppWindow,
		source: "derived",
	},
	{
		kind: "enum",
		key: "matter",
		label: "Matter",
		pluralLabel: "matters",
		icon: Folder,
		source: "derived",
		valueWidthClass: "w-[28rem]",
	},
	{
		kind: "enum",
		key: "client",
		label: "Client",
		pluralLabel: "clients",
		icon: Building2,
		source: "derived",
		valueWidthClass: "w-[22rem]",
	},
	{
		kind: "enum",
		key: "billableCategory",
		label: "Billing",
		pluralLabel: "categories",
		icon: DollarSign,
		source: "static",
		staticValues: ["billable", "billable_credit", "non_billable", "unknown"],
	},
	{
		kind: "enum",
		key: "task",
		label: "Task",
		pluralLabel: "tasks",
		icon: Tags,
		source: "derived",
	},
	{
		kind: "enum",
		key: "activity",
		label: "Activity",
		pluralLabel: "activities",
		icon: Tags,
		source: "derived",
	},
];

export const FIELD_DEF_BY_KEY: Record<FieldKey, FieldDef> = FIELD_DEFS.reduce(
	(acc, def) => {
		acc[def.key] = def;
		return acc;
	},
	{} as Record<FieldKey, FieldDef>,
);

export const ENUM_FIELD_DEF_BY_KEY: Record<EnumFilterField, EnumFieldDef> =
	FIELD_DEFS.filter((d): d is EnumFieldDef => d.kind === "enum").reduce(
		(acc, def) => {
			acc[def.key] = def;
			return acc;
		},
		{} as Record<EnumFilterField, EnumFieldDef>,
	);

// Friendly value labels for static enums whose raw values are snake_case codes.
export const VALUE_LABELS: Partial<
	Record<EnumFilterField, Record<string, string>>
> = {
	status: {
		tracked: "Tracked",
		reviewed: "Reviewed",
		exported: "Exported",
		deleted: "Deleted",
	},
	billableCategory: {
		billable: "Billable",
		non_billable: "Non-billable",
		billable_credit: "Credit",
		unknown: "Unknown",
	},
};

export function getValueLabel(field: EnumFilterField, value: string): string {
	return VALUE_LABELS[field]?.[value] ?? value;
}

export const TEXT_OPERATOR_LABEL = {
	contains: "contains",
	notContains: "does not contain",
} as const;
